import {
  PORTALS,
  SIGN_IN_FAILURE,
  SIGN_IN_STATUS,
  completeMfaChallenge,
  completeMfaEnrolment,
  finishMfaEnrolment,
  signIn,
  startMfaEnrolment,
  signOut,
  unenrollAllSecondFactors,
} from '@client/auth';
import { SET_SIGNED_IN_USER } from '@client/redux/loggedInUser/loggedInUserReducer';
import { handleApiError } from '@client/redux/reduxUtils/reduxUtils';
import { updateCountryFilter } from '../../connectivity/api.loggedInUser';
import {
  LOAD_LOGIN_ERROR,
  MFA_CHALLENGE_REQUIRED,
  MFA_ENROLLED,
  MFA_ENROLMENT_REQUIRED,
  MFA_REMOVED,
  MFA_SECRET_READY,
} from './loggedInUserReducer';

const INVALID_CREDENTIALS_MESSAGE = 'Invalid email or password';
const INVALID_CODE_MESSAGE = 'That code is incorrect. Please try again';
const CODE_EXPIRED_MESSAGE =
  'That code has expired. Enter the current code from your app';
const CHALLENGE_EXPIRED_MESSAGE =
  'Your sign-in has expired. Please sign in again';
const TOO_MANY_ATTEMPTS_MESSAGE =
  'Too many failed attempts. Please wait a moment and try again';
const UNAVAILABLE_MESSAGE =
  'The service is temporarily unavailable. Please try again shortly';

function failureMessage(portal, reason) {
  switch (reason) {
    case SIGN_IN_FAILURE.INVALID_CREDENTIALS:
      return INVALID_CREDENTIALS_MESSAGE;
    case SIGN_IN_FAILURE.INVALID_CODE:
      return INVALID_CODE_MESSAGE;
    case SIGN_IN_FAILURE.CODE_EXPIRED:
      return CODE_EXPIRED_MESSAGE;
    case SIGN_IN_FAILURE.CHALLENGE_EXPIRED:
      return CHALLENGE_EXPIRED_MESSAGE;
    case SIGN_IN_FAILURE.TOO_MANY_ATTEMPTS:
      return TOO_MANY_ATTEMPTS_MESSAGE;
    case SIGN_IN_FAILURE.ACCESS_DENIED:
      return `You do not have access to the ${portal} portal`;
    default:
      return UNAVAILABLE_MESSAGE;
  }
}

// Renders the sign-in outcome the way the old login did: an action with a
// payload on success, one telling the form to ask for the MFA code, or one
// with an error message the login form can show (and the reason behind it).
function renderOutcome(portal, dispatch, outcome) {
  if (outcome.status === SIGN_IN_STATUS.SIGNED_IN) {
    return dispatch(setSignedInUser(outcome.profile));
  }

  if (outcome.status === SIGN_IN_STATUS.MFA_CHALLENGE) {
    return dispatch({ type: MFA_CHALLENGE_REQUIRED });
  }

  if (outcome.status === SIGN_IN_STATUS.MFA_ENROLMENT) {
    return dispatch({ type: MFA_ENROLMENT_REQUIRED });
  }

  if (outcome.status === SIGN_IN_STATUS.MFA_ENROLLED) {
    return dispatch({ type: MFA_ENROLLED });
  }

  if (outcome.status === SIGN_IN_STATUS.MFA_SECRET) {
    const { secretKey, qrCodeUrl } = outcome;

    return dispatch({ type: MFA_SECRET_READY, secretKey, qrCodeUrl });
  }

  return dispatch({
    type: LOAD_LOGIN_ERROR,
    error: failureMessage(portal, outcome.reason),
    reason: outcome.reason,
  });
}

function signInToPortal(portal, email, password) {
  return function(dispatch) {
    return signIn(portal, email, password).then((outcome) =>
      renderOutcome(portal, dispatch, outcome)
    );
  };
}

function submitMfaCode(portal, code) {
  return function(dispatch) {
    return completeMfaChallenge(code).then((outcome) =>
      renderOutcome(portal, dispatch, outcome)
    );
  };
}

export function signInAdmin(email, password) {
  return signInToPortal(PORTALS.ADMIN, email, password);
}

export function submitAdminMfaCode(code) {
  return submitMfaCode(PORTALS.ADMIN, code);
}

function startEnrolment(portal) {
  return function(dispatch) {
    return startMfaEnrolment().then((outcome) =>
      renderOutcome(portal, dispatch, outcome)
    );
  };
}

function submitEnrolmentCode(portal, code, options) {
  return function(dispatch) {
    return completeMfaEnrolment(code, options).then((outcome) =>
      renderOutcome(portal, dispatch, outcome)
    );
  };
}

export function startAdminMfaEnrolment() {
  return startEnrolment(PORTALS.ADMIN);
}

export function submitAdminMfaEnrolmentCode(code) {
  return submitEnrolmentCode(PORTALS.ADMIN, code);
}

export function submitAdvertiserMfaCode(code) {
  return submitMfaCode(PORTALS.ADVERTISER, code);
}

export function startAdvertiserMfaEnrolment() {
  return startEnrolment(PORTALS.ADVERTISER);
}

// An advertiser enrolling from their settings stays signed in until the dialog
// has told them what happens next; finishAdvertiserMfaEnrolment then signs them
// out for the login page.
export function submitAdvertiserMfaEnrolmentCode(code) {
  return submitEnrolmentCode(PORTALS.ADVERTISER, code, { keepSession: true });
}

export function finishAdvertiserMfaEnrolment() {
  return function(dispatch) {
    return finishMfaEnrolment(PORTALS.ADVERTISER).then((outcome) =>
      renderOutcome(PORTALS.ADVERTISER, dispatch, outcome)
    );
  };
}

// The settings page's way to remove the signed-in user's Second Factors: they
// are signed out, and enrol again at their next sign-in.
export function removeMyMfa(portal) {
  return function(dispatch) {
    return unenrollAllSecondFactors().then((outcome) =>
      outcome.status === SIGN_IN_STATUS.MFA_REMOVED
        ? dispatch({ type: MFA_REMOVED })
        : renderOutcome(portal, dispatch, outcome)
    );
  };
}

export function signInAdvertiser(email, password) {
  return signInToPortal(PORTALS.ADVERTISER, email, password);
}

export function setSignedInUser(profile) {
  return { type: SET_SIGNED_IN_USER, payload: profile };
}

// Signs out of Firebase, then does a complete reload to the Portal's login page
// to reset all state.
export function handleLogout(isAdvertiser) {
  return function() {
    return signOut(isAdvertiser ? PORTALS.ADVERTISER : PORTALS.ADMIN);
  };
}

// The saved filter comes back with the admin profile, so a reload is enough to
// apply it everywhere.
export function handleCountryFilter(countryCodes) {
  return function(dispatch) {
    return updateCountryFilter(countryCodes)
      .then(() => {
        location.reload();
      })
      .catch((error) => {
        return handleApiError(dispatch, error, LOAD_LOGIN_ERROR);
      });
  };
}
